import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, ArrowUpRight, BadgeCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { BrandLogo, BRAND_ASSET_PATHS } from '../components/Brand';
import { CompaniesOfficeBlock } from '../components/CompaniesOfficeBlock';

// =============================================================================
// D-061 — GIWAHS Press Kit & Media Centre (review-only)
// Single source of truth for approved brand assets, company facts, and
// founder biography. Mirrors the strict tone of Trust Centre. No public
// gating, no Stripe, no Supabase, no production gate changes.
// Vault doc: /app/memory/founder_vault/42_PRESS_KIT_AND_MEDIA_GUIDELINES.md
// =============================================================================

const APPROVED_DESCRIPTION_SHORT =
  "GIWAHS is a global sourcing intelligence platform — independently verified factories, editorial product trends, and transparent supply data for the world's fashion and product brands.";

const APPROVED_DESCRIPTION_LONG = [
  "GIWAHS is the trading name of Whaitiri Black Limited, a New Zealand registered company. The platform connects retailers, brands, and buyers with independently verified manufacturers worldwide, surfaces editorial product and supply-chain intelligence, and underwrites every claim with a public Trust Centre and a Companies Office-verifiable corporate footprint.",
  "Phase 1 commercial focus is the Founding Partner programme: 500 organisations worldwide, three tiers, contractually protected 10-year price-lock. The roadmap is governed by the GIWAHS Founder AI Execution Standard — autonomous internal construction, founder-controlled production gates.",
];

const BRAND_COLOURS = [
  { name: 'Brand black',     hex: '#000000', usage: 'Canonical brand plate. All logos sit on this colour.' },
  { name: 'Brand gold',      hex: '#DAA520', usage: 'Metallic gold accent in shield, peak of "A", taglines.' },
  { name: 'Brand silver',    hex: '#C0C0C0', usage: 'Globe meridians, "G" monogram, primary wordmark.' },
  { name: 'Tagline gold',    hex: '#A87A2F', usage: 'Warmer gold reserved for "TRUST. CONNECT. TRADE. GROW."' },
  { name: 'Foreground',      hex: '#0A0A0A', usage: 'UI text on light surfaces.' },
  { name: 'Panel',           hex: '#F5F5F0', usage: 'Editorial off-white panel.' },
];

const COMPANY_FACTS = [
  { k: 'Legal name',             v: 'Whaitiri Black Limited' },
  { k: 'Trading as',             v: 'GIWAHS' },
  { k: 'Incorporation',          v: 'New Zealand' },
  { k: 'NZ Companies Office No.', v: '6860633' },
  { k: 'NZBN',                   v: '9429046803306' },
  { k: 'NZ GST number',          v: '125-955-886' },
  { k: 'Director · 100% shareholder', v: 'Jay Elkington' },
  { k: 'Founded',                v: '2026' },
  { k: 'Press contact',          v: 'press@giwahs.com' },
];

const FOUNDER_BIO_SHORT =
  "Jay Elkington is the Founder & Director of Whaitiri Black Limited (trading as GIWAHS). He leads the platform's product, commercial and operating direction, and is the authorised signatory across all Founding Partner contracts.";

const DOWNLOADS = [
  {
    id: 'primary-logo',
    label: 'Primary logo',
    sub: 'Icon + GIWAHS wordmark lockup',
    href: BRAND_ASSET_PATHS.lockup,
    file: 'giwahs-logo.png',
    spec: '975 × 750 · PNG · canonical lockup',
  },
  {
    id: 'secondary-logo',
    label: 'Secondary logo',
    sub: 'Full presentation poster (icon + wordmark + tagline + pillars)',
    href: BRAND_ASSET_PATHS.poster,
    file: 'giwahs-brand-poster.png',
    spec: '1135 × 1254 · PNG · marketing surfaces',
  },
  {
    id: 'brand-icon',
    label: 'Brand icon',
    sub: 'Shield + globe + G monogram only',
    href: BRAND_ASSET_PATHS.icon512,
    file: 'giwahs-icon-512.png',
    spec: '512 × 512 · PNG · app icons, badges, favicons',
  },
  {
    id: 'social-og',
    label: 'Social OG image',
    sub: 'Open-Graph / Twitter share card',
    href: BRAND_ASSET_PATHS.og,
    file: 'giwahs-social-og.png',
    spec: '1200 × 630 · PNG · social previews',
  },
];

const DownloadCard = ({ item }) => (
  <a
    data-testid={`press-download-${item.id}`}
    href={item.href}
    download={item.file}
    className="group flex flex-col gap-4 border border-black bg-white p-6 transition-colors hover:bg-neutral-50"
  >
    <div className="aspect-[4/3] w-full overflow-hidden border border-neutral-200 bg-[#0a0a0a]">
      <img src={item.href} alt={item.label} className="h-full w-full object-contain" />
    </div>
    <div>
      <div className="font-display text-lg" style={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
        {item.label}
      </div>
      <div className="mt-1 text-sm text-neutral-600">{item.sub}</div>
      <div className="mt-3 font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">
        {item.spec}
      </div>
    </div>
    <div className="mt-auto flex items-center justify-between border-t border-neutral-200 pt-4">
      <span className="font-mono-data text-[11px] text-neutral-500">{item.file}</span>
      <span className="inline-flex items-center gap-2 label-overline group-hover:gap-3 transition-all">
        Download <Download size={14} strokeWidth={1.5} />
      </span>
    </div>
  </a>
);

const PressKit = () => {
  return (
    <div data-testid="press-page" className="bg-white">
      <SEO
        title="Press kit & media centre — GIWAHS"
        description="Approved GIWAHS brand assets, company facts, founder biography, and media contact. Whaitiri Black Limited, NZ Co No. 6860633, NZBN 9429046803306."
        noindex
      />

      {/* Hero */}
      <section className="border-b border-black">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-8">
            <span className="label-overline text-neutral-500">/ Press kit & media centre</span>
            <h1
              data-testid="press-hero-headline"
              className="mt-4 font-display tracking-tighter"
              style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.95 }}
            >
              GIWAHS, on the record.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
              Every approved logo, every official fact, every line of biography — in one place,
              under the same brand lock used inside the platform. Use anything you find here
              directly; nothing on this page requires permission.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 border border-black bg-white px-4 py-3">
              <BadgeCheck size={16} strokeWidth={1.7} />
              <span className="label-overline">NZ Co № 6860633 · NZBN 9429046803306</span>
            </div>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <div className="inline-flex flex-col items-start gap-4 border border-black bg-[#0a0a0a] p-6">
              <BrandLogo height={64} link={false} className="md:!h-[88px]" testId="press-hero-logo" />
              <span className="label-overline text-neutral-400">Approved lockup</span>
            </div>
          </div>
        </div>
      </section>

      {/* 01 — Downloads */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <span className="label-overline text-neutral-500">/ 01 — Brand asset downloads</span>
              <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
                The mark, in four files.
              </h2>
              <p className="mt-4 max-w-2xl text-sm text-neutral-700">
                Right-click + download, or click any tile. All assets are PNG with the canonical
                black plate preserved. Do not recolour, recompose, or re-render the artwork.
              </p>
            </div>
            <a
              href={BRAND_ASSET_PATHS.master}
              data-testid="press-master-source-link"
              className="inline-flex items-center gap-2 border-b border-black pb-1 label-overline hover:gap-3 transition-all"
              download
            >
              Download master source (1254×1254) <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {DOWNLOADS.map((item) => <DownloadCard key={item.id} item={item} />)}
          </div>
        </div>
      </section>

      {/* 02 — Brand colours */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-500">/ 02 — Brand colour palette</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
            The palette is sampled, not chosen.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-700">
            These values are sampled directly from the founder-approved source artwork. Use them
            for editorial layouts that need to sit alongside the GIWAHS mark; do not apply them
            to the mark itself.
          </p>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {BRAND_COLOURS.map((c) => (
              <div
                key={c.hex}
                data-testid={`press-colour-${c.hex.toLowerCase()}`}
                className="flex items-stretch gap-4 border border-black bg-white"
              >
                <div
                  className="w-24 shrink-0"
                  style={{ background: c.hex, borderRight: '1px solid #000' }}
                />
                <div className="flex flex-col justify-center py-3 pr-4">
                  <div className="font-display text-base" style={{ fontWeight: 700 }}>{c.name}</div>
                  <div className="mt-0.5 font-mono-data text-[11px] tracking-wider text-neutral-700">{c.hex.toUpperCase()}</div>
                  <div className="mt-1 text-xs text-neutral-600">{c.usage}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 03 — Approved description */}
      <section className="border-b border-black bg-[#0a0a0a] text-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-neutral-500">/ 03 — Approved company description</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
              Use these words.
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-neutral-400">
              Both versions below are pre-approved. Choose by word-count. Edit for tense or
              grammatical fit only — do not paraphrase the claims.
            </p>
          </div>
          <div className="md:col-span-7 space-y-8">
            <div data-testid="press-description-short" className="border border-white p-6">
              <div className="label-overline text-neutral-500">Short — 1 sentence (≈ 40 words)</div>
              <p className="mt-4 text-base leading-relaxed">{APPROVED_DESCRIPTION_SHORT}</p>
            </div>
            <div data-testid="press-description-long" className="border border-white p-6">
              <div className="label-overline text-neutral-500">Long — 2 paragraphs (≈ 130 words)</div>
              {APPROVED_DESCRIPTION_LONG.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed first:mt-4">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 04 — Founder biography */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-neutral-500">/ 04 — Founder biography</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
              Jay Elkington.
            </h2>
            <p className="mt-4 text-sm text-neutral-600">Founder &amp; Director · Whaitiri Black Limited</p>
          </div>
          <div className="md:col-span-7">
            <div data-testid="press-founder-bio" className="border border-black p-6">
              <div className="label-overline text-neutral-500">Short bio (≈ 50 words)</div>
              <p className="mt-4 text-base leading-relaxed text-neutral-800">{FOUNDER_BIO_SHORT}</p>
              <div className="mt-6 border-t border-neutral-200 pt-4 font-mono-data text-[11px] uppercase tracking-wider text-neutral-500">
                Press inquiries · press@giwahs.com · NZ-based · GMT+12/+13
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 05 — Company facts */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline text-neutral-500">/ 05 — Company facts</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
            Independently verifiable.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-neutral-700">
            Each registration is filed with the appropriate New Zealand authority. Use the links
            beneath the table to confirm anything below.
          </p>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse border border-black bg-white text-sm">
              <tbody>
                {COMPANY_FACTS.map((row) => (
                  <tr key={row.k} data-testid={`press-fact-${row.k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <td className="border border-black p-4 font-medium label-overline w-[40%]">{row.k}</td>
                    <td className="border border-black p-4 font-mono-data">{row.v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-neutral-700 font-mono">
            <a
              data-testid="press-verify-companies-office"
              href="https://app.companiesoffice.govt.nz/companies/app/ui/pages/companies/6860633"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline hover:no-underline"
            >
              Verify on Companies Office <ExternalLink size={11} strokeWidth={1.5} />
            </a>
            <span className="text-neutral-300">·</span>
            <a
              data-testid="press-verify-nzbn"
              href="https://www.nzbn.govt.nz/mynzbn/nzbndetails/9429046803306/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 underline hover:no-underline"
            >
              Verify on NZBN Register <ExternalLink size={11} strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </section>

      {/* 06 — Usage rules */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-neutral-500">/ 06 — Usage rules</span>
            <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-5xl" style={{ fontWeight: 700 }}>
              The brand is locked.
            </h2>
            <p className="mt-4 max-w-md text-sm text-neutral-700">
              These rules apply to every public use of the GIWAHS mark — print, screen, video,
              partner co-marketing, and editorial coverage.
            </p>
          </div>
          <div className="md:col-span-7 grid gap-6 md:grid-cols-2">
            <div className="border border-black p-6">
              <div className="label-overline text-neutral-500">Do</div>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-neutral-800">
                <li>· Use only the files served from <code className="bg-neutral-100 px-1">/brand</code>.</li>
                <li>· Keep the black plate around the mark on every surface.</li>
                <li>· Preserve the stylised upward-peak &ldquo;A&rdquo; in <strong>GIWAHS</strong>.</li>
                <li>· Leave clear-space of at least one &ldquo;G&rdquo;-height around the mark.</li>
                <li>· Credit &ldquo;Whaitiri Black Limited (trading as GIWAHS)&rdquo;.</li>
              </ul>
            </div>
            <div className="border border-black p-6">
              <div className="label-overline text-neutral-500">Don&apos;t</div>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-neutral-800">
                <li>· Re-create, redraw, or re-render the mark.</li>
                <li>· Recolour, hue-shift, or invert the metallic gold/silver.</li>
                <li>· Rotate, skew, outline, or apply drop-shadows.</li>
                <li>· Composite with other logos, taglines, or graphics.</li>
                <li>· Use the mark to imply endorsement without written approval.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Companies Office block */}
      <CompaniesOfficeBlock overline="/ 07 — Companies Office verification" />

      {/* Closer */}
      <section className="bg-white">
        <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-6 px-5 py-12 md:flex-row md:items-center md:px-8">
          <div className="font-mono-data text-xs text-neutral-500">
            Brand usage is governed by the GIWAHS Brand Guidelines. For approvals, contact press@giwahs.com.
          </div>
          <Link
            to="/trust"
            data-testid="press-back-to-trust"
            className="inline-flex items-center gap-2 border-b border-black pb-1 label-overline hover:gap-3 transition-all"
          >
            Back to Trust Centre <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PressKit;
